import React from "react";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Favorite, FavoriteBorder, LockOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
  useGetWishlistQuery,
} from "../../Services/api.user.slice";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Store/store";
import toast from "react-hot-toast";
import { checkUserLogin } from "../../types/globalTypes";
import { setToggleAuthModal } from "../../Redux/authReducer";
import { setTogglePropertyClaimedModal } from "../../Redux/propertyReducer";
import { formatToUSNumber } from "../../Utils/formatToUSNumber";
// Card image fallback chain (client request, 2026-06-13):
//   1. featuredPhoto (owner-uploaded) when present
//   2. random MLS photo (blurred) via BlurredMlsPlaceholder, picked
//      deterministically by propertyId so cards don't flicker
//   3. branded webp fallback inside BlurredMlsPlaceholder when no MLS
// The lock for unclaimed properties stays a code overlay (not baked in).
import BlurredMlsPlaceholder from "../BlurredMlsPlaceholder/BlurredMlsPlaceholder";
import type { MlsPhotosContainer } from "../../Utils/mlsPlaceholder";

type PropertyCardProps = {
  imageSrc?: any;
  isClaimed?: boolean;
  featuredPhoto?: string | null;
  propertyName?: any;
  propertyAddress?: any;
  bedCount?: any;
  bathCount?: any;
  sqrFeet?: any;
  wishlist?: any;
  hideBackgdoundColor?: any;
  type?: "product" | "recently" | "services";
  stateName?: string;
  stateId?: string;
  stateZip?: string;
  isLoading?: boolean;
  onClick?: any;
  href?: string;
  id?: string;
  propertyId?: string; // REAPI property ID for wishlist operations
  city?: string;
  mls?: MlsPhotosContainer | null; // for blurred MLS placeholder
};

const PropertyCard = (props: PropertyCardProps) => {
  const [addToWishlist] = useAddToWishlistMutation({});
  const [removeFromWishlist] = useRemoveFromWishlistMutation({});
  const wishlist: any =
    useSelector((state: RootState) => state.auth?.wishlist) || [];
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state?.auth
  );
  // Only fetch wishlist if user is authenticated (prevents unnecessary API calls)
  useGetWishlistQuery("", { skip: !isAuthenticated });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Convert propertyId to string for consistent comparison with wishlist
  const propertyIdToUse = String(props.propertyId || props.id || "");
  const isInWishlist = wishlist.includes(propertyIdToUse);

  async function handleAddToWishlist(event: any) {
    event.preventDefault();
    event.stopPropagation();

    if (!checkUserLogin(user)) {
      toast.error("Please login to add to wishlist");
      dispatch(setToggleAuthModal(true));
      return;
    }
    if (!propertyIdToUse) return;
    try {
      if (!isInWishlist) {
        const response = await addToWishlist({ propertyId: propertyIdToUse }).unwrap();
        if (!response?.isApiError) {
          toast.success(response?.data?.message || "Added to wishlist");
        }
      } else {
        const response = await removeFromWishlist({ propertyId: propertyIdToUse }).unwrap();
        if (!response?.isApiError) {
          toast.success(response?.data?.message || "Removed from wishlist");
        }
      }
      // Tag invalidation in api.user.slice.ts handles refetching automatically
    } catch (error: any) {
      if (error?.data?.isApiError) toast.error(error?.data?.errors?.message);
    }
  }

  const handleProprtyClaimed = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    if (!checkUserLogin(user)) {
      toast.error("Please login to claim property");
      dispatch(setToggleAuthModal(true));
      return;
    }
    // Use propertyId (REAPI ID) for claim requests, fallback to id (MongoDB _id)
    dispatch(setTogglePropertyClaimedModal(props?.propertyId || props?.id));
  };
  //   let _id: String = props.id as string;
  const _isPropertyOwner = user?.propertyList?.indexOf(props.id!) > -1;

  const _makeImageBorderRadius =
    props.type === "recently" || props.type === "services";

  if (props.isLoading) {
    return (
      <Box
        sx={{
          m: {
            xs: 0,
            sm: 3,
            md: 4,
            xl: 6,
          },
        }}
      >
        <Box
          sx={{
            borderRadius: "15px",
            overflow: "hidden",
          }}
        >
          <Stack direction={"column"} height={"100%"} overflow={"hidden"}>
            <Skeleton
              animation="wave"
              variant="rectangular"
              sx={{
                width: "100%",
                height: { xs: "240px", md: "250px", lg: "260px", xl: "275px" },
                borderRadius: props.type === "recently" || props.type === "services" ? "15px" : "0px",
              }}
            />
            <Box p={"25px"}>
              <Stack direction={"column"} spacing={1}>
                <Skeleton animation="wave" width="80%" height={28} />
                <Skeleton animation="wave" width="60%" height={24} />
                {(props.type === "product" || props.type === "recently") && (
                  <>
                    <Skeleton animation="wave" width="70%" height={20} sx={{ mt: 1 }} />
                    {props.type === "product" && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                        <Skeleton animation="wave" variant="rectangular" width="60%" height={40} sx={{ borderRadius: 2 }} />
                        <Skeleton animation="wave" variant="circular" width={40} height={40} />
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component={"a"}
      href={props.href}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        // Service cards can carry a custom action (e.g. the "request a service"
        // bidding flow) instead of navigating to href.
        if (props.type === "services" && props.onClick) {
          props.onClick(e);
          return;
        }
        navigate(props.href!);
      }}
      sx={{ textDecoration: "none", cursor: "pointer" }}
      color={"black"}
    >
      <Box
        // my={2}
        // mx={4}
        borderRadius={"15px"}
        overflow={"hidden"}
        sx={{
          m: {
            xs: 0,
            sm: 3,
            md: 4,
            xl: 6,
          },
          backgroundColor:
            props.type === "recently" || props.type === "services"
              ? "transparent"
              : "backgroundLight2.main",
        }}
      >
        <Stack direction={"column"} height={"100%"} overflow={"hidden"}>
          <Box sx={{ position: "relative" }}>
            {(() => {
              // Source preference for the card image:
              //   - services cards: existing imageSrc (logo)
              //   - claimed with featuredPhoto: owner's featured photo
              //   - otherwise: random MLS photo (blurred) via
              //     BlurredMlsPlaceholder, with the branded webp as its
              //     final fallback when no MLS exists
              const cardHeight = {
                xs: "240px",
                md: "250px",
                lg: "260px",
                xl: "275px",
              };
              const cardBorderRadius = _makeImageBorderRadius ? "15px" : "0px";

              if (props.type === "services" || props.featuredPhoto) {
                return (
                  <Box
                    component="img"
                    src={
                      props.type === "services"
                        ? props.imageSrc
                        : props.featuredPhoto!
                    }
                    alt={props.propertyName}
                    width="100%"
                    sx={{
                      display: "block",
                      borderRadius: cardBorderRadius,
                      objectFit: "cover",
                      height: cardHeight,
                    }}
                  />
                );
              }

              return (
                <Box sx={{ height: cardHeight, width: "100%" }}>
                  <BlurredMlsPlaceholder
                    mls={props.mls}
                    seed={props.propertyId || props.id || ""}
                    alt={props.propertyName || "Property"}
                    borderRadius={cardBorderRadius}
                  />
                </Box>
              );
            })()}
            {/* Lock overlay — shown only on unclaimed property cards (not services). */}
            {props.type !== "services" && !props.isClaimed && (
              <Box
                aria-label="Unclaimed property"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: 2,
                }}
              >
                <LockOutlined sx={{ fontSize: 22 }} />
              </Box>
            )}
          </Box>
          <Box p={"25px"}>
            <Box>
              <Typography fontWeight={"600"} variant="h6" color="#072020">
                {props.propertyName}
              </Typography>
              {props.type === "product" || props.type === "recently" ? (
                <Typography
                  fontWeight={"400"}
                  variant="subtitle1"
                  lineHeight={1}
                  fontSize={18}
                  color="#072020"
                >
                  {props?.city}, {props?.stateName} {props.stateZip}
                </Typography>
              ) : (
                <Typography
                  fontWeight={"400"}
                  variant="subtitle1"
                  lineHeight={1}
                  fontSize={18}
                  color="#072020"
                >
                  Starting from $49
                </Typography>
              )}
            </Box>

            {props.type === "product" || props.type === "recently" ? (
              <Box mt={"20px"}>
                <Stack direction={"column"} spacing={"10px"}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={"400"}
                    fontSize={18}
                    color="#072020"
                  >
                    {`
                        ${
                          props?.bedCount > 0 ? `${props?.bedCount} beds` : ""
                        }  
                        ${
                          props?.bathCount > 0
                            ? `${props?.bathCount} baths`
                            : ""
                        }  
                        ${
                          props?.sqrFeet > 0
                            ? `${formatToUSNumber(props?.sqrFeet)} sq ft`
                            : ""
                        }  
                        `}
                  </Typography>
                  <Box>
                    {props.type === "product" ? (
                      <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                      >
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={handleProprtyClaimed}
                          disabled={_isPropertyOwner}
                          sx={{
                            borderRadius: 2,
                            p: "4px 25px",
                          }}
                        >
                          <Typography
                            fontWeight={"400"}
                            variant="subtitle1"
                            fontSize={18}
                          >
                            I own this property
                          </Typography>
                        </Button>

                        <IconButton
                          onClick={(event) => handleAddToWishlist(event)}
                        >
                          {isInWishlist ? (
                            <Favorite fontSize="large" color="primary" />
                          ) : (
                            <FavoriteBorder
                              fontSize="large"
                              sx={{ fill: "#0e0e0e" }}
                            />
                          )}
                        </IconButton>
                      </Stack>
                    ) : (
                      ""
                    )}
                  </Box>
                </Stack>
              </Box>
            ) : (
              ""
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default PropertyCard;
